import { GraduationCap, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

export const PROVIDERS = [
  { name: "Amazon Web Services", tag: ["Cloud e Generative AI", "Cloud and Generative AI"], certs: ["Generative AI Developer · Professional", "AI Practitioner", "Cloud Practitioner", "Solutions Architect", "Machine Learning Engineer"] },
  { name: "Microsoft", tag: ["Azure, AI e formazione", "Azure, AI and training"], certs: ["Azure AI Engineer Associate", "AZ-204 Developer", "AZ-104 Administrator", "AZ-305 Solutions Architect", "AZ-400 DevOps Engineer", "AI-900 AI Fundamentals", "Certified Trainer (MCT)"] },
  { name: "Google Cloud", tag: ["Generative AI", "Generative AI"], certs: ["Generative AI Leader", "Generative AI for Developers"] },
  { name: "GitHub", tag: ["Agenti AI", "AI agents"], certs: ["Agentic AI Developer"] },
  { name: "MIT", tag: ["Machine Learning", "Machine Learning"], certs: ["Professional Certificate in ML & AI"] },
  { name: "Stanford", tag: ["Intelligenza artificiale", "Artificial intelligence"], certs: ["AI Graduate Certificate"] },
  { name: "Harvard", tag: ["Informatica per l'AI", "Computer science for AI"], certs: ["Professional Certificate in CS for AI"] },
];


const Intro = ({ tr }) => {
  const total = PROVIDERS.reduce((n, p) => n + p.certs.length, 0);
  return (
    <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-mono text-[#E0262C] mb-6">
        <GraduationCap className="w-3.5 h-3.5" /> {tr("Percorso di formazione", "Training path")}
      </div>
      <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] text-[#09090b]">
        {tr("Un team che non smette di", "A team that never stops")} <span className="aurora-text">{tr("studiare", "learning")}</span>
      </h2>
      <p className="mt-5 text-base text-[#52525b] leading-relaxed">
        {tr("Investiamo in formazione continua su intelligenza artificiale e cloud. Ecco i percorsi di certificazione in corso o pianificati; lo stato di ciascuno è verificabile su richiesta.",
          "We invest in continuous training on artificial intelligence and cloud. These are the certification paths in progress or planned; the status of each is verifiable on request.")}
      </p>
      <div className="mt-10 grid grid-cols-2 border-t border-[#09090b]">
        <div data-testid="cert-stat-providers" className="pt-5 pr-4 border-r border-[#e4e4e7]">
          <div className="font-display text-5xl text-[#09090b]">{PROVIDERS.length}</div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mt-2">{tr("Enti formativi", "Institutions")}</div>
        </div>
        <div data-testid="cert-stat-paths" className="pt-5 pl-5">
          <div className="font-display text-5xl text-[#09090b]">{total}</div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mt-2">{tr("Percorsi", "Paths")}</div>
        </div>
      </div>
    </div>
  );
};

const ProviderRow = ({ p, i, tr }) => (
  <li data-testid={`cert-provider-${i}`} className="group grid sm:grid-cols-12 gap-4 py-6 border-b border-[#e4e4e7] hover:bg-[#fafafa] transition-colors duration-300 cert-row" style={{ animationDelay: `${i * 70}ms` }}>
    <div className="sm:col-span-4 flex gap-4">
      <span className="font-mono text-xs text-[#a1a1aa] pt-1.5 group-hover:text-[#E0262C] transition-colors">{String(i + 1).padStart(2, "0")}</span>
      <div>
        <div className="font-display text-xl text-[#09090b] leading-tight">{p.name}</div>
        <div className="text-xs text-[#71717a] mt-1">{tr(p.tag[0], p.tag[1])}</div>
      </div>
    </div>
    <div className="sm:col-span-8 flex flex-wrap gap-2 content-start">
      {p.certs.map((c) => (
        <span key={c} className="inline-flex items-center gap-1.5 text-xs text-[#27272a] border border-[#e4e4e7] bg-white rounded-full px-3 py-1.5 hover:border-[#E0262C] hover:text-[#E0262C] transition-colors">
          <span className="w-1.5 h-1.5 rounded-full bg-[#E0262C]/70" />{c}
        </span>
      ))}
    </div>
  </li>
);

export const CertificationsSection = () => {
  const { tr } = useLang();
  return (
    <section data-testid="certifications-section" className="bg-white border-t border-[#e4e4e7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Intro tr={tr} />
        <div className="lg:col-span-8">
          <div className="flex items-center justify-between pb-3 border-b border-[#09090b] text-[10px] uppercase tracking-widest font-mono text-[#71717a]">
            <span>{tr("Ente", "Institution")}</span>
            <span className="inline-flex items-center gap-1">{tr("Certificazioni in corso", "Certifications in progress")} <ArrowUpRight className="w-3 h-3" /></span>
          </div>
          <ul>{PROVIDERS.map((p, i) => <ProviderRow key={p.name} p={p} i={i} tr={tr} />)}</ul>
          <p className="text-[10px] text-[#a1a1aa] mt-6">{tr("Loghi e badge sono marchi dei rispettivi proprietari, mostrati solo per indicare i percorsi formativi; nessuna affiliazione o sponsorizzazione.", "Logos and badges are trademarks of their respective owners, shown only to indicate training paths; no affiliation or endorsement.")}</p>
        </div>
      </div>
    </section>
  );
};
